/**
 * Finished-good / linked-inventory policy toggles.
 *
 *
 * Callers should go through `inventory-utils.js` rather than reading policy
 * inline for stock/CTA decisions.
 */

/**
 * @typedef {'range-replacement'|'replacement'|'category'} InventoryCarouselSource
 * @typedef {'finished-good'|'master'} InventoryCarouselTileSubject
 */

export const INVENTORY_POLICY = Object.freeze({
  /**
   * Want It Sooner / in-stock carousel source of truth.
   * - `range-replacement` (confirmed): category masters → their replacement FGs
   * - `replacement`: current PDP replacement links only (rollback)
   * - `category`: legacy productSearch ATS > 0 hits (force-only)
   * @type {InventoryCarouselSource}
   */
  CAROUSEL_SOURCE: 'range-replacement',

  /**
   * When `range-replacement` yields no in-stock FGs, fall back to today’s
   * category ATS > 0 hits. Turn off once middleware
   * and category assignment always populate replacement links.
   */
  CAROUSEL_CATEGORY_FALLBACK: true,

  /**
   * What each Want It Sooner tile is.
   * - `finished-good` (confirmed): tile = in-stock FG; click → FG PDP / ATC that id
   * - `master`: later switch — tile = master that has ≥1 in-stock replacement FG
   * @type {InventoryCarouselTileSubject}
   */
  CAROUSEL_TILE_SUBJECT: 'finished-good',

  /**
   * When using replacement / range-replacement feed, only show products with ATS > 0.
   */
  CAROUSEL_FILTER_ATS_GT_ZERO: true,

  /**
   * When linked finished good is in stock, Family A ATC adds the finished-good
   * product id (not the MTO cover variant). Jasper master ATC stays MTO.
   */
  ATC_ADD_FINISHED_GOOD_WHEN_IN_STOCK: true,

  /**
   * Notify Me stays the existing Formcrafts redirect URL.
   */
  NOTIFY_ME_KEEP_FORMCRAFTS: true,

  /**
   * Respect warehouse inventoryIds when reading finished-good ATS.
   */
  RESPECT_WAREHOUSE_INVENTORY_IDS: true,

  /**
   * Unlinked cover (no inventoryProductId): OOS + Notify Me (not disabled ATC).
   */
  UNLINKED_VARIANT_DISABLE_ATC: false,

  /**
   * Keep Low stock (ATS 1–4) messaging; quantities come from linked FG / FG own inventory.
   */
  KEEP_LOW_STOCK_THRESHOLDS: true,
});

/** Product link type used for in-stock finished-good carousel discovery. */
export const REPLACEMENT_PRODUCT_LINK_TYPE = 'replacement';
